import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchPartnerDealerGroupedInventory } from "../../../services/automapPartnerDealerApi";
import {
  isGroupedStatusSupported,
  mapFlatStatusToGroupedStatus,
} from "../../../utils/partnerInventoryFormatters";
import {
  PARTNER_INVENTORY_ANALYTICS_EVENTS,
  trackPartnerInventoryAnalyticsEvent,
} from "../../../utils/partnerInventoryAnalytics";
import PartnerInventoryMakeGroup from "./PartnerInventoryMakeGroup";
import PartnerInventoryUnclassifiedBucket from "./PartnerInventoryUnclassifiedBucket";

function SummaryCards({ summary, totalListings }) {
  const cards = [
    { label: "Total listings", value: totalListings ?? 0 },
    { label: "Make groups", value: summary?.makeGroups ?? 0 },
    { label: "Model groups", value: summary?.modelGroups ?? 0 },
    { label: "Trim groups", value: summary?.trimGroups ?? 0 },
    { label: "Needs review", value: summary?.needsReviewCount ?? 0 },
    { label: "Unclassified", value: summary?.unclassifiedCount ?? 0 },
  ];

  return (
    <div className="partner-normalization-review__summary partner-grouped-summary">
      {cards.map((card) => (
        <div key={card.label} className="partner-normalization-review__summary-card">
          <p className="partner-normalization-review__summary-value">{card.value}</p>
          <p className="partner-normalization-review__summary-label">{card.label}</p>
        </div>
      ))}
    </div>
  );
}

export default function PartnerGroupedInventoryView({
  dealerId,
  path,
  search = "",
  status = "",
  source = "",
  normalizationStatus = "",
  vehicleIdentityMetrics = null,
  normalizationReviewEnabled = true,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const groupedStatus = mapFlatStatusToGroupedStatus(status);
  const statusSupported = isGroupedStatusSupported(status);

  const reviewPath = useMemo(
    () => `${path(`dealers/${dealerId}/inventory/normalization`)}?status=partial`,
    [dealerId, path]
  );

  const loadGroupedInventory = useCallback(async () => {
    if (!statusSupported) {
      setLoading(false);
      setData(null);
      setError("");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const result = await fetchPartnerDealerGroupedInventory(dealerId, {
        groupBy: "make,model,trim",
        source: source || "all",
        status: groupedStatus || "all",
        normalizationStatus: normalizationStatus || undefined,
        includeListings: true,
        limitListingsPerGroup: 10,
        search: search.trim() || undefined,
      });
      setData(result);
      trackPartnerInventoryAnalyticsEvent(PARTNER_INVENTORY_ANALYTICS_EVENTS.GROUPED_LOADED, {
        dealerId,
        viewMode: "grouped",
        totalListings: result.totalListings ?? vehicleIdentityMetrics?.totalListings ?? null,
        makeModelCoverage: vehicleIdentityMetrics?.makeModelCoverage ?? null,
        needsReviewRate: vehicleIdentityMetrics?.needsReviewRate ?? null,
      });
    } catch (err) {
      setError(err.message || "Unable to load grouped inventory.");
      setData(null);
      trackPartnerInventoryAnalyticsEvent(PARTNER_INVENTORY_ANALYTICS_EVENTS.GROUPED_ERROR, {
        dealerId,
        viewMode: "grouped",
        errorMessage: err.message || "Unable to load grouped inventory.",
      });
    } finally {
      setLoading(false);
    }
  }, [
    dealerId,
    groupedStatus,
    normalizationStatus,
    search,
    source,
    statusSupported,
    vehicleIdentityMetrics,
  ]);

  useEffect(() => {
    loadGroupedInventory();
  }, [loadGroupedInventory]);

  if (!statusSupported) {
    return (
      <div className="partner-grouped-status-note">
        <p className="muted">
          Grouped view supports <strong>All statuses</strong>, <strong>Active</strong>, and{" "}
          <strong>Sold</strong> filters only. Switch status or return to the flat list for draft/paused
          listings.
        </p>
      </div>
    );
  }

  if (loading) {
    return <p className="muted">Loading grouped inventory…</p>;
  }

  if (error) {
    return (
      <div className="partner-grouped-error">
        <p className="admin-data-health-error">
          We couldn&apos;t load grouped inventory. The flat list is still available.
        </p>
        <p className="muted">{error}</p>
        <button type="button" className="midpoint-share-button" onClick={loadGroupedInventory}>
          Retry
        </button>
      </div>
    );
  }

  const hasGroups = (data?.groups || []).length > 0;
  const hasUnclassified = (data?.unclassified?.count || 0) > 0;
  const isEmpty = !data?.totalListings;

  if (isEmpty) {
    return (
      <div className="partner-dealers-empty">
        <p className="muted">
          {search.trim()
            ? "No grouped inventory matches your search."
            : "No inventory found for this dealer."}
        </p>
      </div>
    );
  }

  if (!hasGroups && !hasUnclassified) {
    return (
      <div className="partner-dealers-empty">
        <p className="muted">No grouped inventory matches your search.</p>
      </div>
    );
  }

  return (
    <div className="partner-grouped-inventory">
      <SummaryCards summary={data.summary} totalListings={data.totalListings} />

      <div className="partner-grouped-inventory__actions">
        {normalizationReviewEnabled ? (
          <Link className="cv-button-link" to={reviewPath}>
            Fix vehicle names
          </Link>
        ) : null}
      </div>

      <div className="partner-grouped-inventory__tree">
        {(data.groups || []).map((makeGroup, index) => (
          <PartnerInventoryMakeGroup
            key={`${makeGroup.make?.id || makeGroup.make?.label}-${index}`}
            makeGroup={makeGroup}
            dealerId={dealerId}
            path={path}
            reviewPath={reviewPath}
          />
        ))}

        <PartnerInventoryUnclassifiedBucket
          title="Unclassified"
          count={data.unclassified?.count || 0}
          reason={data.unclassified?.reason}
          listings={data.unclassified?.listings || []}
          dealerId={dealerId}
          path={path}
          reviewPath={reviewPath}
          prominent
          warningLabel="Needs review"
          showBulkNormalizeHint
        />
      </div>
    </div>
  );
}
